// Share-web app state — pub/sub store.
//
// Mirrors the desktop `app/renderer/state.js` idiom but with a typed,
// callback-based subscribe API (Set of listeners, idempotent unsubscribe).
// `setState` shallow-merges a patch into the state, then notifies every
// subscriber AFTER the mutation so callbacks always observe the new value.

import type { ShareManifest, SummaryPeaks } from '@bandway/share-types';

export interface TrackUiState {
  id: string;
  gain: number;
  muted: boolean;
  soloed: boolean;
}

export interface AppState {
  manifest: ShareManifest | null;
  peaks: SummaryPeaks | null;
  loaded: boolean;
  playing: boolean;
  playhead: number;
  trackStates: Map<string, TrackUiState>;
  currentSectionId: string | null;
  error: { kind: string; message: string } | null;
  // Promo modal slot — F.1+F.2+F.3 shell. Topbar nav buttons set
  // {open:true, feature:<key>}; G.1 mounts the actual modal listening on it.
  promoModal: { open: boolean; feature?: string };
  // 53-я shell additions
  sidebarOpen: boolean;
  mode: 'rehearsal' | 'stage';
  shareDialog: { open: boolean };
  /**
   * Tier 3 section loop — id of the section currently being looped, or null
   * when no loop is active. Player polls this via setActiveLoopSection on
   * change; section-loop-binding toggles `.is-loop-active` class accordingly.
   * Set by section-handlers gestures (dblclick / touch longpress / Escape).
   */
  activeLoopId: string | null;
}

function makeInitialState(): AppState {
  return {
    manifest: null,
    peaks: null,
    loaded: false,
    playing: false,
    playhead: 0,
    trackStates: new Map(),
    currentSectionId: null,
    error: null,
    promoModal: { open: false },
    // 53-я
    sidebarOpen: false,
    mode: 'rehearsal',
    shareDialog: { open: false },
    activeLoopId: null,
  };
}

let _state: AppState = makeInitialState();
const _subscribers = new Set<(state: AppState) => void>();

export function getState(): AppState {
  return _state;
}

export function setState(patch: Partial<AppState>): void {
  _state = { ..._state, ...patch };
  // Snapshot subscribers so an unsubscribe-during-notify doesn't skip listeners.
  for (const fn of Array.from(_subscribers)) {
    fn(_state);
  }
}

export function subscribe(fn: (state: AppState) => void): () => void {
  _subscribers.add(fn);
  // Fire immediately with the current state so callers don't need a separate
  // `getState()` call on mount.
  fn(_state);
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    _subscribers.delete(fn);
  };
}

export function __resetForTests(): void {
  _state = makeInitialState();
  _subscribers.clear();
}
